import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Crown, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePremiumCredits } from '@/hooks/usePremiumCredits';
import { PremiumCreditsDisplay } from '@/components/PremiumCreditsDisplay';

interface Evaluation {
  id: string;
  score: number;
  description: string;
  image_data: string;
  created_at: string;
  insights: any;
  has_premium: boolean;
}

const History = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const { hasPremium, creditsRemaining, loading: creditsLoading } = usePremiumCredits();

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    loadEvaluations();
  }, [user, navigate]);

  const loadEvaluations = async () => {
    try {
      const { data: evalData, error: evalError } = await supabase
        .from('evaluations')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (evalError) throw evalError;

      const { data: purchases, error: purchaseError } = await supabase
        .from('premium_purchases')
        .select('evaluation_id')
        .eq('user_id', user?.id);

      if (purchaseError) throw purchaseError;

      const purchasedIds = new Set(purchases?.map(p => p.evaluation_id) || []);
      
      const evaluationsWithPremium = evalData?.map(evaluation => ({
        ...evaluation,
        has_premium: purchasedIds.has(evaluation.id) || evaluation.insights !== null
      })) || [];

      setEvaluations(evaluationsWithPremium);
    } catch (error) {
      toast.error('Error loading evaluations');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back', 'Back')}
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Your Tree Evaluations</h1>
          <p className="text-muted-foreground">
            {evaluations.length} {evaluations.length === 1 ? 'evaluation' : 'evaluations'}
          </p>
          <div className="mt-4">
            <PremiumCreditsDisplay 
              hasPremium={hasPremium}
              creditsRemaining={creditsRemaining}
              loading={creditsLoading}
            />
          </div>
        </div>

        {evaluations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No evaluations yet</p>
              <Button onClick={() => navigate('/camera')}>
                Rate Your First Tree
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {evaluations.map((evaluation) => (
              <Card
                key={evaluation.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/result?evaluation=${evaluation.id}`)}
              >
                <div className="flex flex-col md:flex-row gap-4">
                  <img
                    src={evaluation.image_data}
                    alt={`Tree rated ${evaluation.score}/10`}
                    className="w-full md:w-48 h-48 object-cover rounded-l-lg"
                  />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-2xl font-bold">
                            {evaluation.score}/10
                          </Badge>
                          {evaluation.has_premium && (
                            <Badge variant="default" className="bg-gradient-to-r from-yellow-500 to-orange-500">
                              <Crown className="h-3 w-3 mr-1" />
                              Premium
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {evaluation.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground mt-4">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(evaluation.created_at)}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
